'use client';

import { ChevronRight } from 'lucide-react';
import { useTranslations } from 'next-intl';
import { useEffect, useState } from 'react';

import { GmLevelCoin } from '@/components/entities/gamification/GmLevelCoin';
import { GmProgressBar } from '@/components/entities/gamification/GmProgressBar';
import { GmStatusImage } from '@/components/entities/gamification/GmStatusImage';
import { GmBarDesktopSkeleton } from '@/components/entities/gamification/skeletons/GmBarDesktopSkeleton';
import { AppRoutes } from '@/i18n/constants';
import { Link } from '@/i18n/routing';
import { useAppSelector } from '@/redux/hooks';
import { cn } from '@/utils/cn';
import { useGmBarPlayerData } from '../../../model/hooks/useGmBarPlayerData';
import { getStatusImagePath } from '../../../model/utils/getProgressStatusAssetMedia';
import GmBarDesktopStub from './GmBarDesktopStub';

export const GmBarDesktop = () => {
  const t = useTranslations('gamification.widget-bar');
  const { progress, playerProfile, isLoading } = useGmBarPlayerData();
  const userSession = useAppSelector(state => state.user.userSession);

  const [isHydrated, setIsHydrated] = useState(false);
  const showLoadingState = !isHydrated || isLoading;

  const statusTitle = playerProfile?.progressStatus?.name || '';
  const statusPicture = playerProfile?.progressStatus ? getStatusImagePath(playerProfile?.progressStatus) : null;

  const isGuest = isHydrated && !userSession;
  const currentLevelXp = (progress?.totalXp ?? 0) - (progress?.currentLevelXpRequired ?? 0);
  const levelXpRequired = (progress?.nextLevelXpRequired ?? 0) - (progress?.currentLevelXpRequired ?? 0);

  useEffect(() => {
    setIsHydrated(true);
  }, []);

  if (showLoadingState) {
    return <GmBarDesktopSkeleton />;
  }

  return (
    <div className="relative 3xl:mx-px flex flex-col gap-3 overflow-hidden rounded-2xl glass-stroke-border gm-sidebar-status-card-bg p-4 shadow-[1px_2px_10px_0_rgba(0,0,0,0.55)]">
      {isGuest && <GmBarDesktopStub />}
      <div className={cn('relative z-1 flex gap-3 flex-col', isGuest && 'blur-xs')}>

        <div className="flex w-full min-h-11 items-center gap-2">
          {
            statusPicture && (
              <div className="relative size-11 shrink-0 overflow-visible">
                <GmStatusImage
                  src={statusPicture}
                  alt={statusTitle || 'Status icon'}
                  className="pointer-events-none size-11 select-none"
                />
              </div>
            )
          }

          <p className="min-w-0 flex-1 text-sm font-medium leading-none text-white">
            {t('your-status', { status: statusTitle })}
          </p>

          <Link href={AppRoutes.gamificationAllStages} className="flex w-8.75 group shrink-0 items-center justify-end text-xs font-medium leading-4 text-c300">
            <span className="transition-colors duration-300 group-hover:text-c100">{t('all-statuses')}</span>
            <ChevronRight className="size-4 transition-colors duration-300 group-hover:text-c100" aria-hidden />
          </Link>
        </div>

        <Link
          aria-label={t('view-missions')}
          className="flex w-full flex-col group"
          href={AppRoutes.gamificationMissions}
        >
          <div className="relative h-7.25 w-full">
            <GmLevelCoin levelNumber={progress?.level ?? 0} className="relative z-1" appearance="desktopWidget" />
            <div className="absolute inset-x-0 left-4 top-1/2 z-0 -translate-y-1/2">
              <GmProgressBar percent={progress?.percent ?? 0} showLightEffect={false} appearance="desktopWidget" />
            </div>
          </div>

          <div className="mt-0.5 flex w-full justify-between text-xs font-medium leading-4 whitespace-nowrap">
            <span className="text-c300">{t('your-level')}</span>
            <span className="gm-sidebar-status-xp-text group-hover:text-white duration-300 transition-colors">
              {currentLevelXp}
              /
              {levelXpRequired}
              {' '}
              XP
            </span>
          </div>
        </Link>

      </div>
    </div>
  );
};
